import type { FC } from 'react';
import React from 'react';
import { Box, Flex, Heading, Glyph } from '@edreamsodigeo/prisma-design-system';
import { useTranslation } from '@edreamsodigeo/frontend-core';

import type { CompletedSegment } from '@/components/FlightSummary/Segment/resolver';
import type { TransportType } from '@/generated/graphql';

interface IconsProps {
  PLANE: React.ReactElement;
  TRAIN: React.ReactElement;
}

const icons: IconsProps = {
  PLANE: (
    <Glyph
      key={1}
      color="flightProductColor"
      size="medium"
      category="Planes"
      name="FlightRight"
    />
  ),
  TRAIN: (
    <Glyph
      key={0}
      color="trainProductColor"
      size="medium"
      category="TravelProducts"
      name="Train"
    />
  ),
};

const keys: Record<string, string> = {
  PLANE: 'flightSummary.itinerary.header.flight',
  TRAIN: 'flightSummary.itinerary.header.train',
};

const getTransportKeys = (segments: Array<CompletedSegment>): Array<string> =>
  Object.keys(keys).filter((key) =>
    segments.some(
      (segment) =>
        segment.transportTypes &&
        segment.transportTypes.length &&
        segment.transportTypes.indexOf(key as TransportType) > -1
    )
  );

const TitleHeaderMapItinerary: FC<{ segments: Array<CompletedSegment> }> = ({
  segments,
}) => {
  const { t } = useTranslation();
  const transportKeys = getTransportKeys(segments);
  const transportType = transportKeys.map((key) => t(keys[key])).join(' & ');

  return (
    <Flex
      mb="1x_big"
      mt="small"
      mx="base"
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex alignItems="center">
        <Heading type="3" color="neutral.100">
          {t('flightSummary.itinerary.header.title')}
        </Heading>
        <Box ml="2x_small">
          <Heading type="3" color="neutral.80">
            {transportType}
          </Heading>
        </Box>
      </Flex>
      <Flex>
        {transportKeys.map((key, index) => (
          <Flex
            key={`transport-${key}-${index}`}
            data-testid={`${key.toLowerCase()}-icon`}
            px="1x_small"
            py="1x_small"
            borderRadius={5}
            backgroundColor="bgSecondary"
            ml="1x_small"
          >
            {icons[key]}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export default TitleHeaderMapItinerary;
